import { Injectable, ApplicationRef, NgZone } from '@angular/core'

@Injectable({
  providedIn: 'root',
})
export class ChangeDetectionService {
  private isTyping = false
  private typingTimeout: ReturnType<typeof setTimeout> | undefined = undefined
  private typingResetTimeout: ReturnType<typeof setTimeout> | undefined = undefined
  
  // Performance optimization: adaptive debouncing based on input type
  private lastInputTime = 0
  private originalTick: (() => void) | null = null
  private isChangeDetectionDisabled = false
  
  // Different debounce times for different scenarios
  private readonly TYPING_DEBOUNCE = 150 // Reduced from 300ms
  private readonly FAST_DEBOUNCE = 75   // For quick inputs
  private readonly MAX_DEBOUNCE = 250   // Maximum wait time
  
  constructor(
    private appRef: ApplicationRef,
    private zone: NgZone
  ) {}

  handleGlobalInput(inputElement?: HTMLElement) {
    const now = performance.now()
    const timeSinceLastInput = now - this.lastInputTime
    this.lastInputTime = now
    
    // Cancel the previous typing reset timeout if it exists
    if (this.typingResetTimeout) {
      clearTimeout(this.typingResetTimeout)
    }

    // Adaptive debouncing: use shorter delays for rapid typing
    let debounceDelay = this.TYPING_DEBOUNCE
    if (timeSinceLastInput < 100) {
      // User is typing rapidly, use shorter debounce
      debounceDelay = this.FAST_DEBOUNCE
    }
    
    // Determine if this is a critical input that needs faster response
    const isCriticalInput = this.isCriticalInput(inputElement)
    if (isCriticalInput) {
      debounceDelay = Math.min(debounceDelay, this.FAST_DEBOUNCE)
    }

    // If not already typing, disable change detection
    if (!this.isTyping) {
      this.disableChangeDetection()
    }
    
    this.isTyping = true

    // Set timeout to reset typing flag with adaptive delay
    this.typingResetTimeout = setTimeout(() => {
      this.isTyping = false
    }, debounceDelay)
  }

  debounceReattach() {
    // Clear previous timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
    }

    // Calculate adaptive debounce time
    const now = performance.now()
    const timeSinceLastInput = now - this.lastInputTime
    
    let reattachDelay = this.TYPING_DEBOUNCE
    
    // If user stopped typing recently, use shorter delay
    if (timeSinceLastInput < 200) {
      reattachDelay = this.FAST_DEBOUNCE
    }
    
    // Ensure we don't wait too long
    reattachDelay = Math.min(reattachDelay, this.MAX_DEBOUNCE)

    // Reattach change detection after adaptive delay
    this.typingTimeout = setTimeout(() => {
      this.reattachChangeDetection()
    }, reattachDelay)
  }
  
  private disableChangeDetection(): void {
    if (this.isChangeDetectionDisabled) {return}
    
    this.zone.runOutsideAngular(() => {
      // Store original tick function
      this.originalTick = this.appRef.tick.bind(this.appRef)
      // Override app tick temporarily with no-op
      this.appRef.tick = () => {}
      this.isChangeDetectionDisabled = true
    })
  }
  
  private reattachChangeDetection(): void {
    if (!this.isChangeDetectionDisabled) {return}
    
    this.zone.run(() => {
      // Restore original tick function
      if (this.originalTick) {
        this.appRef.tick = this.originalTick
        this.originalTick = null
      } else {
        // Fallback: restore from prototype
        this.appRef.tick = ApplicationRef.prototype.tick
      }
      
      this.isChangeDetectionDisabled = false
      
      // Trigger a single tick to check for changes
      this.appRef.tick()
    })
  }
  
  private isCriticalInput(element?: HTMLElement): boolean {
    if (!element) {return false}
    
    // Check for search inputs, autocomplete, or other critical fields
    const classList = element.classList
    const inputType = (element as HTMLInputElement).type
    
    // Critical input types that need faster response
    return (
      classList.contains('search') ||
      classList.contains('autocomplete') ||
      classList.contains('typeahead') ||
      inputType === 'search' ||
      element.hasAttribute('data-critical') ||
      // Check parent elements for critical contexts
      element.closest('[data-critical], .search-container, .autocomplete-container') !== null
    )
  }
  
  // Method to force immediate reattachment (useful for critical operations)
  forceReattach(): void {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
    }
    if (this.typingResetTimeout) {
      clearTimeout(this.typingResetTimeout)
    }
    
    this.isTyping = false
    this.reattachChangeDetection()
  }
  
  // Method to check if currently in typing mode (useful for other services)
  isCurrentlyTyping(): boolean {
    return this.isTyping
  }
  
  // Cleanup method
  destroy(): void {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
    }
    if (this.typingResetTimeout) {
      clearTimeout(this.typingResetTimeout)
    }
    
    if (this.isChangeDetectionDisabled) {
      this.reattachChangeDetection()
    }
  }
}
